import type {
  TcRegion,
  TcTcpsApiService,
  ViewDetailsResponse,
} from '@data-access/connect/tc-tcps';
import type {
  DataLoadingService,
  FeatureExtractionContextType,
  ProjectDataState,
} from '@mobile-mapping/providers';
import type { ViewMode } from '@mobile-mapping/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import type { SetStoreFunction } from 'solid-js/store';
import type { ILogger } from '@trimble/logging';
import { asError } from '@trimble/logging';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import {
  MeasurementsCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  ViewsCapabilityProvider,
  ViewWithClipping,
  convertScan3dStyleToPointCloudStyling,
} from '@trimble/web3d-providers';
import { ClippingCapability } from '@trimble/web3d-capabilities';
import { PaneSyncer } from '@trimble/pane-sync';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { Scan3dStyle } from '@trimble/types';
import type { MultipaneReservedData } from './multipane-view.types';
import { applyMultipaneLayout, applyPaneState } from './apply-multipane-layout';
import {
  applyNavigationStates,
  applyPerPaneViewData,
  applyTrajectories,
} from './apply-multipane-view-data';
import { applyCadEditingState } from './apply-cad-editing-state';
import { parseMultipaneReservedData } from './parse-multipane-reserved-data';
import { fromReservedMeasurements } from './reserved-measurements';
import { restoreMeasurementLabels } from './measurement-labels';

export type LoadViewWithReservedDataDeps = {
  readonly tcTcpsApi: Pick<TcTcpsApiService, 'getReservedViewData'>;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly dataLoadingService: DataLoadingService;
  readonly dataState: ProjectDataState;
  readonly layouts: PaneLayouts<ViewMode>;
  readonly setLayouts: SetStoreFunction<PaneLayouts<ViewMode>>;
  readonly region: TcRegion;
  readonly logger: ILogger;
  readonly featureExtractionContext?: FeatureExtractionContextType;
};

let currentLoadId = 0;
let loadingSuppressionOwner: number | null = null;

/**
 * Loads a saved view with numark reserved data: layout → pane state → shared
 * data → trajectories → navigation states → per-pane data.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 26 > 15 allowed; flagged for later refactor, not fixed here
export async function loadViewWithReservedData(
  view: ViewDetailsResponse,
  deps: LoadViewWithReservedDataDeps
): Promise<void> {
  const {
    tcTcpsApi,
    capabilityRegistry,
    dataLoadingService,
    dataState,
    layouts,
    setLayouts,
    region,
    logger,
    featureExtractionContext,
  } = deps;

  const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider)();
  if (!viewsProvider) return;

  const loadId = ++currentLoadId;
  const stillCurrent = (): boolean => loadId === currentLoadId;
  // A superseded load is not allowed to clear pane-sync suppression, so a
  // newer load must drop that flag before its own reserved-data fetch.
  // Otherwise a slow or hung fetch leaves `isLoadingView` stuck on.
  if (loadingSuppressionOwner !== null) {
    loadingSuppressionOwner = null;
    PaneSyncer.setIsLoadingView(false);
  }

  let reserved: MultipaneReservedData | null = null;
  let reservedOutdated = false;
  try {
    const dto = await tcTcpsApi.getReservedViewData(region, view.id, 'numark');
    const parsed = parseMultipaneReservedData(dto.data);
    reserved = parsed.data;
    reservedOutdated = parsed.outdated;
  } catch (error: unknown) {
    logger.debug(
      'Reserved view data unavailable, using single pane',
      undefined,
      asError(error)
    );
  }

  if (!stillCurrent()) return;

  if (reservedOutdated) {
    NotificationEventBus.emit({
      type: 'warning',
      message: t('mobileMapping.views.outdatedReservedData'),
    });
  }

  // Connect 3D (TRCPS) stores scan ids and scan3dStyle on reserved app=trcps,
  // not on scan-data view_{id}. Numark views keep using scan-data via loadView.
  let trcpsScans: unknown = null;
  if (!reserved) {
    try {
      const trcpsDto = await tcTcpsApi.getReservedViewData(
        region,
        view.id,
        'trcps'
      );
      trcpsScans =
        trcpsDto.data && typeof trcpsDto.data === 'object'
          ? trcpsDto.data['scans']
          : null;
    } catch (error: unknown) {
      logger.debug(
        'TRCPS reserved view data unavailable',
        undefined,
        asError(error)
      );
    }
  }
  const trcpsScanIds = extractScanIdsFromTrcpsReserved(trcpsScans);

  if (!stillCurrent()) return;

  try {
    if (reserved) {
      loadingSuppressionOwner = loadId;
      PaneSyncer.setIsLoadingView(true);
    }
    applyMultipaneLayout(layouts, setLayouts, reserved);
    await applyPaneState(capabilityRegistry, reserved);
    if (!stillCurrent()) return;

    await viewsProvider.loadView(view, region, {
      skipCamera: !!reserved,
    });
    if (!stillCurrent()) return;

    if (!reserved && trcpsScanIds.length > 0) {
      const scan3dStyle = extractScan3dStyleFromTrcpsReserved(trcpsScans);
      const stylingProvider = capabilityRegistry.get(
        PointCloudStylingCapabilityProvider
      )();
      if (scan3dStyle && stylingProvider) {
        stylingProvider.clearUserClassOverrides();
        stylingProvider.setPointCloudStyling(
          convertScan3dStyleToPointCloudStyling(
            scan3dStyle,
            stylingProvider.defaultStyling()
          )
        );
      }
      dataLoadingService.loadExclusive('pointCloud', trcpsScanIds);
    }

    // Alignment (and other non-Connect-native) measurements live in
    // reserved data, so restore them after loadView re-seeds the native
    // markups. A failure here must not abort the rest of the view load.
    if (reserved?.measurements?.length) {
      try {
        const measurementsProvider = capabilityRegistry.get(
          MeasurementsCapabilityProvider
        )();
        measurementsProvider?.restoreMeasurements(
          fromReservedMeasurements(reserved.measurements)
        );
      } catch (error: unknown) {
        logger.error(
          `Failed to restore measurements for view ${view.id}`,
          undefined,
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'warning',
          message: t('mobileMapping.views.measurementsRestoreFailed'),
        });
      }
    }

    // Runs even without reserved data so legacy/Connect-only views still get
    // names; a failure must not abort the rest of the load.
    try {
      const measurementsProvider = capabilityRegistry.get(
        MeasurementsCapabilityProvider
      )();
      if (measurementsProvider) {
        restoreMeasurementLabels(
          measurementsProvider,
          reserved?.connectMeasurementData ?? []
        );
      }
    } catch (error: unknown) {
      logger.error(
        `Failed to restore measurement labels for view ${view.id}`,
        undefined,
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.views.measurementNamesRestoreFailed'),
      });
    }

    // Reserved data applies clippings pane by pane; otherwise restore here.
    if (!reserved) {
      const viewWithClipping = view as ViewWithClipping;
      for (const pane of capabilityRegistry.paneContainers()) {
        const clippingCap = pane.get(ClippingCapability)();
        if (clippingCap) {
          clippingCap.loadClippingFromConnectData(
            view.sectionPlanes,
            viewWithClipping.sectionBox
          );
        }
      }
    }

    await applyTrajectories(dataLoadingService, dataState, reserved);
    if (!stillCurrent()) return;

    await applyNavigationStates(capabilityRegistry, reserved, stillCurrent);
    if (!stillCurrent()) return;

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        if (!stillCurrent()) {
          resolve();
          return;
        }
        resolve(
          applyPerPaneViewData(capabilityRegistry, reserved, stillCurrent)
        );
      });
    });
  } finally {
    if (loadingSuppressionOwner === loadId) {
      loadingSuppressionOwner = null;
      PaneSyncer.setIsLoadingView(false);
    }
  }

  // Runs after pane-sync suppression is released: waiting on layers here
  // must not hold camera/navigation sync suppression for up to 15s.
  if (!stillCurrent() || !featureExtractionContext) return;

  // cadEditingMode false means no CAD state, so restore hides all layers.
  if (featureExtractionContext.isFeatureExtractionEnabled()) {
    const cadEditing =
      reserved?.cadEditing && reserved.cadEditing.cadEditingMode !== false
        ? reserved.cadEditing
        : { visibleLayerIds: [] };
    await applyCadEditingState(
      featureExtractionContext,
      cadEditing,
      logger,
      stillCurrent
    );
  }
}

const SCAN3D_STYLE_KEYS = [
  'renderingMode',
  'blendIntensity',
  'showEyeDomeLighting',
  'pointBudget',
  'pointDensity',
  'pointSize',
  'classificationSchemes',
  'intensityOffset',
  'intensityStartColor',
  'intensityEndColor',
  'clearanceDefaultTransparent',
  'clearanceDefaultColor',
  'clearanceBands',
  'elevationReference',
  'elevationTolerance',
  'elevationBandCount',
  'elevationOffset',
] as const;

function extractScanIdsFromTrcpsReserved(scans: unknown): string[] {
  if (!Array.isArray(scans)) {
    return [];
  }

  const ids: string[] = [];
  for (const scan of scans) {
    if (typeof scan === 'string' && scan !== '') {
      ids.push(scan);
      continue;
    }
    if (hasNonEmptyStringId(scan)) {
      ids.push(scan.id);
    }
  }
  return ids;
}

function extractScan3dStyleFromTrcpsReserved(
  scans: unknown
): Scan3dStyle | undefined {
  if (!Array.isArray(scans)) {
    return undefined;
  }

  for (const scan of scans) {
    if (!hasScan3dStyleProperty(scan)) {
      continue;
    }
    if (isScan3dStyle(scan.scan3dStyle)) {
      return scan.scan3dStyle;
    }
  }

  return undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasNonEmptyStringId(value: unknown): value is { id: string } {
  return (
    isPlainObject(value) &&
    typeof value['id'] === 'string' &&
    value['id'] !== ''
  );
}

function hasScan3dStyleProperty(
  value: unknown
): value is { scan3dStyle: unknown } {
  return isPlainObject(value) && 'scan3dStyle' in value;
}

function isScan3dStyle(value: unknown): value is Scan3dStyle {
  if (!isPlainObject(value)) {
    return false;
  }
  return SCAN3D_STYLE_KEYS.some((key) => key in value);
}
